/**
 * Central Dashboard Data Configuration
 * 
 * This file contains all the data used across dashboard components.
 * Edit this file to update data displayed in the application.
 * 
 * Data can be connected to APIs by replacing static values with API calls.
 */

// ============================================
// REGION CONFIGURATION
// ============================================
export const REGION_CONFIG = {
  default: 'dmv',
  mapCenter: [38.9072, -77.0369], // [lat, lon] for API calls (Washington DC)
  regions: {
    dmv: {
      id: 'dmv',
      name: 'DMV Region (DC, Maryland, Virginia)',
      center: [-77.0369, 38.9072], // Washington DC [lon, lat] for map
      zoom: 8,
      counties: ['Montgomery County', 'Fairfax County', 'Prince George\'s County', 'Loudoun County', 'Howard County']
    },
    central: {
      id: 'central',
      name: 'Central Region',
      center: [-77.5, 39.0],
      zoom: 8,
      counties: ['Frederick County', 'Carroll County', 'Washington County']
    },
    southern: {
      id: 'southern',
      name: 'Southern Region',
      center: [-76.8, 38.5],
      zoom: 8,
      counties: ['Calvert County', 'St. Mary\'s County', 'Charles County']
    }
  }
};

// ============================================
// CROP DATA
// ============================================
export const CROP_DATA = {
  // Main crops grown in the region
  crops: [
    { id: 'corn', name: 'Corn', area: 450, unit: 'acres', percentage: 38, color: '#ffc107' },
    { id: 'soybeans', name: 'Soybeans', area: 355, unit: 'acres', percentage: 30, color: '#4caf50' },
    { id: 'hay', name: 'Hay', area: 178, unit: 'acres', percentage: 15, color: '#9c27b0' },
    { id: 'wheat', name: 'Wheat', area: 142, unit: 'acres', percentage: 12, color: '#2196f3' },
    { id: 'vegetables', name: 'Vegetables', area: 59, unit: 'acres', percentage: 5, color: '#f44336' }
  ],

  // Yield data by crop
  yields: {
    corn: { current: 4.2, previous: 3.9, unit: 'tons/acre', change: '+7.7%' },
    soybeans: { current: 2.8, previous: 2.6, unit: 'tons/acre', change: '+7.7%' },
    wheat: { current: 2.5, previous: 2.3, unit: 'tons/acre', change: '+8.7%' }
  },

  // Crop health by county
  healthByCounty: [
    { county: 'Montgomery County', health: 92, status: 'excellent' },
    { county: 'Fairfax County', health: 87, status: 'good' },
    { county: 'Prince George\'s County', health: 79, status: 'moderate' },
    { county: 'Loudoun County', health: 90, status: 'excellent' },
    { county: 'Howard County', health: 85, status: 'good' }
  ]
};

// ============================================
// WEATHER DATA
// ============================================
export const WEATHER_DATA = {
  current: {
    temperature: 22, // Celsius
    humidity: 68, // Percentage
    precipitation: 5, // mm in last 24h
    windSpeed: 12, // km/h
    windDirection: 'NE',
    pressure: 1013, // hPa
    uvIndex: 6,
    visibility: 10, // km
    cloudCover: 30 // Percentage
  },

  // 14-day forecast for pest prediction
  forecast: Array.from({ length: 14 }, (_, i) => ({
    date: new Date(Date.now() + i * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    temperature: 20 + Math.round(Math.random() * 10),
    humidity: 60 + Math.round(Math.random() * 25),
    precipitation: Math.round(Math.random() * 15),
    windSpeed: 5 + Math.round(Math.random() * 15)
  })),

  // Conditions rating (0-100)
  conditions: {
    temperature: 75,
    humidity: 68,
    windSpeed: 35,
    precipitation: 40,
    sunlight: 80,
    soilMoisture: 65
  }
};

// ============================================
// BIODIVERSITY DATA
// ============================================
export const BIODIVERSITY_DATA = {
  // Species counts (for Shannon/Simpson index calculation)
  speciesCounts: [45, 32, 28, 22, 18, 15, 12, 10, 8, 6, 5, 4, 3, 2, 1],

  // Component scores (0-100)
  habitatQualityScore: 72,
  farmingPracticesScore: 68,
  conservationScore: 65,

  // Crop distribution for monoculture risk
  cropDistribution: [
    { crop: 'Corn', area: 450 },
    { crop: 'Wheat', area: 280 },
    { crop: 'Soybeans', area: 180 },
    { crop: 'Cover Crops', area: 90 },
    { crop: 'Vegetables', area: 45 },
    { crop: 'Fruits', area: 35 }
  ],

  // Historical scores for trend
  historicalScores: [
    { month: 'Jul', score: 62 },
    { month: 'Aug', score: 64 },
    { month: 'Sep', score: 65 },
    { month: 'Oct', score: 67 },
    { month: 'Nov', score: 68 },
    { month: 'Dec', score: 70 },
    { month: 'Jan', score: 72 }
  ]
};

// ============================================
// SATELLITE / NDVI DATA
// ============================================
export const SATELLITE_DATA = {
  // NDVI values for field (simulated pixel data)
  ndviValues: Array.from({ length: 100 }, () => 0.3 + Math.random() * 0.4),

  // Previous period for change detection
  previousNDVI: Array.from({ length: 100 }, () => 0.25 + Math.random() * 0.4),

  // Time series NDVI (12 months)
  timeSeries: Array.from({ length: 12 }, (_, i) => ({
    date: new Date(2025, i, 15).toISOString(),
    ndvi: 0.3 + Math.sin(i * Math.PI / 6) * 0.2 + Math.random() * 0.1
  })),

  // Last imagery info
  lastImagery: {
    source: 'Sentinel-2',
    date: '2026-01-15',
    cloudCover: 12,
    resolution: '10m'
  },

  // Fields for selection
  fields: [
    { id: 'field-1', name: 'Field A - North Section', area: 45 },
    { id: 'field-2', name: 'Field B - Central', area: 32 },
    { id: 'field-3', name: 'Field C - South Section', area: 28 }
  ]
};

// ============================================
// PEST DATA
// ============================================
export const PEST_DATA = {
  // Current pest observations
  observations: [
    { pest: 'Aphids', severity: 'low', lastSeen: '2026-01-10', location: 'Field A' },
    { pest: 'Stem Borer', severity: 'moderate', lastSeen: '2026-01-08', location: 'Field B' },
    { pest: 'Rust (Fungal)', severity: 'low', lastSeen: '2026-01-05', location: 'Field C' }
  ],

  // Historical outbreaks for accuracy tracking
  historicalOutbreaks: [
    { pestId: 'aphids', date: '2025-05-15', region: 'dmv', severity: 'high' },
    { pestId: 'stem_borer', date: '2025-07-20', region: 'dmv', severity: 'moderate' }
  ],

  // Crops being monitored
  monitoredCrops: ['Corn', 'Wheat', 'Soybeans', 'Rice']
};

// ============================================
// SUSTAINABILITY DATA
// ============================================
export const SUSTAINABILITY_DATA = {
  overallScore: 77,
  complianceStatus: 'Compliant',
  lastUpdated: new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),

  dimensions: [
    { name: 'Soil Health', score: 85, trend: 'improving' },
    { name: 'Water Management', score: 72, trend: 'stable' },
    { name: 'Biodiversity', score: 68, trend: 'improving' },
    { name: 'Pest Management', score: 76, trend: 'stable' },
    { name: 'Climate Resilience', score: 62, trend: 'declining' },
    { name: 'Energy Efficiency', score: 79, trend: 'improving' },
    { name: 'Crop Rotation', score: 88, trend: 'improving' },
    { name: 'Waste Reduction', score: 81, trend: 'stable' },
    { name: 'Carbon Sequestration', score: 65, trend: 'improving' },
    { name: 'Community Engagement', score: 90, trend: 'stable' },
    { name: 'Economic Viability', score: 77, trend: 'improving' },
    { name: 'Resource Sharing', score: 83, trend: 'improving' }
  ]
};

// ============================================
// CLIMATE DATA
// ============================================
export const CLIMATE_DATA = {
  currentClimate: {
    avgTemp: 22,
    annualPrecip: 800,
    extremeHeatDays: 15,
    frostFreeDays: 180,
    precipVariability: 20
  },

  projections: {
    2030: { tempChange: '+1.2°C', precipChange: '-5%' },
    2050: { tempChange: '+2.0°C', precipChange: '-10%' },
    2100: { tempChange: '+2.7°C', precipChange: '-15%' }
  }
};

// ============================================
// POLICY DATA
// ============================================
export const POLICY_DATA = {
  systemMetrics: {
    biodiversityScore: 68,
    monocultureRisk: 42,
    yieldTrend: -3,
    waterAvailability: 75,
    pestRiskScore: 35
  }
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get crop data for charts
 */
export const getCropChartData = () => ({
  labels: CROP_DATA.crops.map(c => c.name),
  data: CROP_DATA.crops.map(c => c.percentage),
  colors: CROP_DATA.crops.map(c => c.color)
});

/**
 * Get weather conditions for radar chart
 */
export const getWeatherRadarData = () => ({
  labels: Object.keys(WEATHER_DATA.conditions).map(k =>
    k.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())
  ),
  current: Object.values(WEATHER_DATA.conditions),
  optimal: [72, 65, 45, 50, 75, 70]
});

/**
 * Get crop health by county
 */
export const getCropHealthData = () => ({
  labels: CROP_DATA.healthByCounty.map(c => c.county),
  data: CROP_DATA.healthByCounty.map(c => c.health),
  statuses: CROP_DATA.healthByCounty.map(c => c.status)
});

const dashboardData = {
  REGION_CONFIG,
  CROP_DATA,
  WEATHER_DATA,
  BIODIVERSITY_DATA,
  SATELLITE_DATA,
  PEST_DATA,
  SUSTAINABILITY_DATA,
  CLIMATE_DATA,
  POLICY_DATA,
  getCropChartData,
  getWeatherRadarData,
  getCropHealthData
};

export default dashboardData;
